import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Upload, Download, Eye, Trash2, Check, X, FileText, Filter,
  Search, BarChart3, AlertCircle, Clock, CheckCircle2, XCircle, File
} from "lucide-react";

const STATE_CONFIG = {
  pendiente: { label: "Pendiente", color: "#f59e0b", bg: "bg-amber-50 border-amber-200 text-amber-700" },
  recibida: { label: "Recibida", color: "#3b82f6", bg: "bg-blue-50 border-blue-200 text-blue-700" },
  validada: { label: "Validada", color: "#22c55e", bg: "bg-green-50 border-green-200 text-green-700" },
  rechazada: { label: "Rechazada", color: "#ef4444", bg: "bg-red-50 border-red-200 text-red-700" },
};

const STATE_ICONS = {
  pendiente: Clock,
  recibida: Eye,
  validada: CheckCircle2,
  rechazada: XCircle,
};

export default function DocumentsTab({ axiosAuth, departments = [], centers = [], initialFilter }) {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fEmployee, setFEmployee] = useState("all");
  const [fCategory, setFCategory] = useState("all");
  const [fState, setFState] = useState(initialFilter === "docs_pending" ? "pendiente" : "all");
  const [fCenter, setFCenter] = useState("all");
  const [fDept, setFDept] = useState("all");
  const [fSearch, setFSearch] = useState("");

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ employee_id: "", category: "otro", description: "", expiry_date: "", request_id: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Detail modal
  const [selected, setSelected] = useState(null);

  // Preview modal
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState("");

  // Reject modal
  const [rejectDoc, setRejectDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  // Requests for linking
  const [requests, setRequests] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/hr/documents/categories");
      setCategories(res.data.categories || []);
    } catch {}
  }, [axiosAuth]);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/employees");
      setEmployees(res.data || []);
    } catch {}
  }, [axiosAuth]);

  const loadRequests = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/vacation-requests");
      setRequests(res.data || []);
    } catch {}
  }, [axiosAuth]);

  const loadStats = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/hr/documents/stats");
      setStats(res.data);
    } catch {}
  }, [axiosAuth]);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fEmployee !== "all") params.employee_id = fEmployee;
      if (fCategory !== "all") params.category = fCategory;
      if (fState !== "all") params.doc_state = fState;
      if (fCenter !== "all") params.center_id = fCenter;
      if (fDept !== "all") params.department_id = fDept;
      const res = await axiosAuth().get("/hr/documents", { params });
      setDocs(res.data || []);
    } catch { toast.error("Error al cargar documentos"); }
    finally { setLoading(false); }
  }, [axiosAuth, fEmployee, fCategory, fState, fCenter, fDept]);

  useEffect(() => { loadCategories(); loadEmployees(); loadRequests(); }, [loadCategories, loadEmployees, loadRequests]);
  useEffect(() => { loadDocs(); loadStats(); }, [loadDocs, loadStats]);

  const catMap = {};
  categories.forEach(c => { catMap[c.value] = c.label; });
  const empMap = {};
  employees.forEach(e => { empMap[e.id] = e; });
  const deptMap = {};
  departments.forEach(d => { deptMap[d.id] = d.name; });
  const centerMap = {};
  centers.forEach(c => { centerMap[c.id] = c.name; });

  // Text search filter (client-side)
  const filtered = docs.filter(d => {
    if (!fSearch) return true;
    const s = fSearch.toLowerCase();
    return (d.employee_name || "").toLowerCase().includes(s)
      || (d.filename || "").toLowerCase().includes(s)
      || (d.description || "").toLowerCase().includes(s)
      || (d.category_label || "").toLowerCase().includes(s);
  });

  const handleUpload = async () => {
    if (!uploadForm.employee_id) { toast.error("Selecciona un empleado"); return; }
    if (!uploadFile) { toast.error("Selecciona un archivo"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("employee_id", uploadForm.employee_id);
      fd.append("category", uploadForm.category);
      fd.append("description", uploadForm.description);
      fd.append("expiry_date", uploadForm.expiry_date);
      fd.append("request_id", uploadForm.request_id);
      await axiosAuth().post("/hr/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido correctamente");
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ employee_id: "", category: "otro", description: "", expiry_date: "", request_id: "" });
      loadDocs();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir documento"); }
    finally { setUploading(false); }
  };

  const handleValidate = async (docId) => {
    try {
      await axiosAuth().put(`/hr/documents/${docId}/validate`);
      toast.success("Documento validado");
      loadDocs(); loadStats();
      if (selected?.id === docId) setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al validar"); }
  };

  const handleReject = async () => {
    if (!rejectDoc) return;
    try {
      await axiosAuth().put(`/hr/documents/${rejectDoc.id}/reject`, { reason: rejectReason });
      toast.success("Documento rechazado");
      setRejectDoc(null);
      setRejectReason("");
      loadDocs(); loadStats();
      if (selected?.id === rejectDoc.id) setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al rechazar"); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Eliminar este documento permanentemente?")) return;
    try {
      await axiosAuth().delete(`/hr/documents/${docId}`);
      toast.success("Documento eliminado");
      loadDocs(); loadStats();
      if (selected?.id === docId) setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const handlePreview = async (doc) => {
    const previewable = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!previewable.includes(doc.content_type)) { toast.error("Tipo no previsualizable"); return; }
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/preview`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.content_type }));
      setPreviewUrl(url);
      setPreviewType(doc.content_type);
    } catch { toast.error("Vista previa no disponible"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  };
  const formatSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <div className="space-y-5" data-testid="documents-tab">
      {/* KPI Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="doc-stats">
          {[
            { label: "Total", value: stats.total, icon: FileText, cls: "text-slate-700 bg-slate-50 border-slate-200" },
            { label: "Pendientes", value: stats.pendiente, icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-200" },
            { label: "Recibidos", value: stats.recibida, icon: Eye, cls: "text-blue-700 bg-blue-50 border-blue-200" },
            { label: "Validados", value: stats.validada, icon: CheckCircle2, cls: "text-green-700 bg-green-50 border-green-200" },
            { label: "Rechazados", value: stats.rechazada, icon: XCircle, cls: "text-red-700 bg-red-50 border-red-200" },
            { label: "Sol. sin docs", value: stats.requests_pending_docs, icon: AlertCircle, cls: stats.requests_pending_docs > 0 ? "text-orange-700 bg-orange-50 border-orange-300" : "text-green-700 bg-green-50 border-green-200" },
          ].map(k => (
            <Card key={k.label} className={`border rounded-sm ${k.cls}`} data-testid={`doc-stat-${k.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-3 text-center">
                <k.icon className="w-4 h-4 mx-auto mb-1 opacity-60" />
                <p className="text-2xl font-bold font-mono">{k.value}</p>
                <p className="text-[10px] uppercase tracking-wider font-semibold mt-0.5">{k.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Toolbar: Filters + Upload */}
      <div className="flex flex-wrap gap-2 items-end" data-testid="doc-filters">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={fSearch} onChange={e => setFSearch(e.target.value)} placeholder="Buscar nombre, archivo..." className="pl-7 h-8 text-xs rounded-sm" data-testid="doc-search" />
        </div>
        <Select value={fEmployee} onValueChange={setFEmployee}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-44" data-testid="doc-filter-employee"><SelectValue placeholder="Empleado" /></SelectTrigger>
          <SelectContent className="max-h-60">
            <SelectItem value="all">Todos los empleados</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCategory} onValueChange={setFCategory}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-40" data-testid="doc-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorias</SelectItem>
            {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fState} onValueChange={setFState}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-36" data-testid="doc-filter-state"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCenter} onValueChange={setFCenter}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-40" data-testid="doc-filter-center"><SelectValue placeholder="Centro" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los centros</SelectItem>
            {centers.filter(c => c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fDept} onValueChange={setFDept}>
          <SelectTrigger className="h-8 text-xs rounded-sm w-40" data-testid="doc-filter-dept"><SelectValue placeholder="Departamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los deptos.</SelectItem>
            {departments.filter(d => d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="outline" className="text-[10px] rounded-sm h-8 flex items-center">{filtered.length} docs</Badge>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm ml-auto" onClick={() => setShowUpload(true)} data-testid="btn-upload-doc">
          <Upload className="w-3 h-3 mr-1" /> Subir Documento
        </Button>
      </div>

      {/* Table */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase">Empleado</TableHead>
                  <TableHead className="text-[10px] uppercase">Archivo</TableHead>
                  <TableHead className="text-[10px] uppercase">Categoria</TableHead>
                  <TableHead className="text-[10px] uppercase">Estado</TableHead>
                  <TableHead className="text-[10px] uppercase">Subido por</TableHead>
                  <TableHead className="text-[10px] uppercase">Fecha</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400 text-xs">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400 text-xs">Sin documentos</TableCell></TableRow>
                ) : filtered.map(d => {
                  const st = STATE_CONFIG[d.state] || STATE_CONFIG.pendiente;
                  const StIcon = STATE_ICONS[d.state] || Clock;
                  return (
                    <TableRow key={d.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(d)} data-testid={`doc-row-${d.id}`}>
                      <TableCell>
                        <div>
                          <p className="text-xs font-semibold">{d.employee_name || "-"}</p>
                          <p className="text-[10px] text-slate-400">{centerMap[d.center_id] || ""}{d.department_id ? ` / ${deptMap[d.department_id] || ""}` : ""}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <File className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <div>
                            <p className="text-xs truncate max-w-[180px]" title={d.filename}>{d.filename}</p>
                            <p className="text-[10px] text-slate-400">{formatSize(d.size_bytes)}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{d.category_label || catMap[d.category] || d.category}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[9px] ${st.bg}`}>
                          <StIcon className="w-2.5 h-2.5 mr-0.5" /> {st.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{d.uploaded_by || "-"}</TableCell>
                      <TableCell className="text-[10px] font-mono text-slate-500">{formatDate(d.created_at)}</TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          {["application/pdf", "image/jpeg", "image/png", "image/jpg"].includes(d.content_type) && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500 hover:text-blue-600" onClick={() => handlePreview(d)} title="Vista previa" data-testid={`btn-preview-${d.id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(d)} title="Descargar" data-testid={`btn-download-${d.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {d.state !== "validada" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-green-600 hover:text-green-700" onClick={() => handleValidate(d.id)} title="Validar" data-testid={`btn-validate-${d.id}`}>
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {d.state !== "rechazada" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => { setRejectDoc(d); setRejectReason(""); }} title="Rechazar" data-testid={`btn-reject-${d.id}`}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => handleDelete(d.id)} title="Eliminar" data-testid={`btn-delete-${d.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm uppercase tracking-wider">Subir Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Empleado *</Label>
              <Select value={uploadForm.employee_id || "_none"} onValueChange={v => setUploadForm(p => ({ ...p, employee_id: v === "_none" ? "" : v }))}>
                <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="upload-employee"><SelectValue placeholder="Seleccionar empleado..." /></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="_none">- Seleccionar -</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Categoria</Label>
              <Select value={uploadForm.category} onValueChange={v => setUploadForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vincular a solicitud</Label>
              <Select value={uploadForm.request_id || "_none"} onValueChange={v => setUploadForm(p => ({ ...p, request_id: v === "_none" ? "" : v }))}>
                <SelectTrigger className="h-8 text-xs rounded-sm" data-testid="upload-request"><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="_none">- Sin vincular -</SelectItem>
                  {requests.filter(r => !uploadForm.employee_id || r.employee_id === uploadForm.employee_id).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.employee_name} - {r.request_type} ({r.start_date})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Descripcion</Label>
              <Textarea value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))} className="text-xs rounded-sm h-16" placeholder="Descripcion del documento..." data-testid="upload-description" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fecha de caducidad</Label>
              <Input type="date" value={uploadForm.expiry_date} onChange={e => setUploadForm(p => ({ ...p, expiry_date: e.target.value }))} className="h-8 text-xs rounded-sm" data-testid="upload-expiry" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Archivo *</Label>
              <div
                className="border-2 border-dashed border-slate-300 rounded-sm p-4 text-center cursor-pointer hover:border-slate-400 transition-colors"
                onClick={() => fileRef.current?.click()}
                data-testid="upload-dropzone"
              >
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <File className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-medium">{uploadFile.name}</span>
                    <span className="text-[10px] text-slate-400">({formatSize(uploadFile.size)})</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-6 h-6 mx-auto text-slate-400 mb-1" />
                    <p className="text-xs text-slate-500">Haz clic para seleccionar archivo</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">PDF, JPG, PNG, DOCX, XLSX (max 15MB)</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setShowUpload(false)}>Cancelar</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs rounded-sm" onClick={handleUpload} disabled={uploading} data-testid="btn-confirm-upload">
              {uploading ? "Subiendo..." : "Subir Documento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="doc-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sm uppercase tracking-wider flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Detalle del Documento
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase text-slate-400 font-semibold">Empleado</p>
                    <p className="text-xs font-medium" data-testid="detail-employee">{selected.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-400 font-semibold">Categoria</p>
                    <p className="text-xs font-medium">{selected.category_label || catMap[selected.category] || selected.category}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-400 font-semibold">Archivo</p>
                    <p className="text-xs font-medium truncate" title={selected.filename}>{selected.filename}</p>
                    <p className="text-[10px] text-slate-400">{formatSize(selected.size_bytes)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-400 font-semibold">Estado</p>
                    <Badge variant="outline" className={`text-[9px] ${(STATE_CONFIG[selected.state] || STATE_CONFIG.pendiente).bg}`}>
                      {(STATE_CONFIG[selected.state] || STATE_CONFIG.pendiente).label}
                    </Badge>
                  </div>
                  {selected.description && (
                    <div className="col-span-2">
                      <p className="text-[10px] uppercase text-slate-400 font-semibold">Descripcion</p>
                      <p className="text-xs">{selected.description}</p>
                    </div>
                  )}
                  {selected.expiry_date && (
                    <div>
                      <p className="text-[10px] uppercase text-slate-400 font-semibold">Caducidad</p>
                      <p className="text-xs font-mono">{formatDate(selected.expiry_date)}</p>
                    </div>
                  )}
                </div>
                {/* Traceability */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-[10px] uppercase text-slate-400 font-semibold tracking-wider">Trazabilidad</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-slate-50 rounded-sm p-2">
                      <p className="text-[10px] text-slate-400">Subido por</p>
                      <p className="font-medium" data-testid="detail-uploaded-by">{selected.uploaded_by || "-"}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{formatDateTime(selected.uploaded_at || selected.created_at)}</p>
                    </div>
                    {selected.validated_by && (
                      <div className="bg-slate-50 rounded-sm p-2">
                        <p className="text-[10px] text-slate-400">{selected.state === "rechazada" ? "Rechazado por" : "Validado por"}</p>
                        <p className="font-medium" data-testid="detail-validated-by">{selected.validated_by}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{formatDateTime(selected.validated_at)}</p>
                      </div>
                    )}
                  </div>
                  {selected.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-2">
                      <p className="text-[10px] text-red-500 font-semibold uppercase">Motivo de rechazo</p>
                      <p className="text-xs text-red-700" data-testid="detail-rejection-reason">{selected.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-3 gap-1">
                <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => handleDownload(selected)} data-testid="detail-download">
                  <Download className="w-3 h-3 mr-1" /> Descargar
                </Button>
                {selected.state !== "validada" && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm" onClick={() => handleValidate(selected.id)} data-testid="detail-validate">
                    <Check className="w-3 h-3 mr-1" /> Validar
                  </Button>
                )}
                {selected.state !== "rechazada" && (
                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs rounded-sm" onClick={() => { setRejectDoc(selected); setRejectReason(""); }} data-testid="detail-reject">
                    <X className="w-3 h-3 mr-1" /> Rechazar
                  </Button>
                )}
                <Button size="sm" variant="outline" className="text-slate-400 border-slate-200 hover:text-red-500 text-xs rounded-sm" onClick={() => handleDelete(selected.id)} data-testid="detail-delete">
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDoc} onOpenChange={() => setRejectDoc(null)}>
        <DialogContent className="max-w-sm" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm uppercase tracking-wider text-red-600">Rechazar Documento</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Documento: <span className="font-medium text-slate-700">{rejectDoc?.filename}</span></p>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Motivo del rechazo</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="text-xs rounded-sm h-20" placeholder="Indicar motivo..." data-testid="reject-reason" />
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" className="text-xs rounded-sm" onClick={() => setRejectDoc(null)}>Cancelar</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-sm" onClick={handleReject} data-testid="btn-confirm-reject">
              Rechazar Documento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {previewUrl && (
        <Dialog open={!!previewUrl} onOpenChange={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
          <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="preview-dialog">
            <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">Vista previa</DialogTitle></DialogHeader>
            {previewType === "application/pdf" ? (
              <iframe src={previewUrl} className="w-full h-[65vh] rounded-sm" title="Preview" />
            ) : (
              <img src={previewUrl} alt="Preview" className="max-w-full max-h-[65vh] object-contain mx-auto rounded-sm" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Mini component for embedding in employee dialog
export function EmployeeDocuments({ axiosAuth, employeeId, employeeName }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ category: "otro", description: "", expiry_date: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/hr/documents", { params: { employee_id: employeeId } });
      setDocs(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [axiosAuth, employeeId]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/hr/documents/categories");
      setCategories(res.data.categories || []);
    } catch {}
  }, [axiosAuth]);

  useEffect(() => { if (employeeId) { loadDocs(); loadCategories(); } }, [employeeId, loadDocs, loadCategories]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error("Selecciona un archivo"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("employee_id", employeeId);
      fd.append("category", uploadForm.category);
      fd.append("description", uploadForm.description);
      fd.append("expiry_date", uploadForm.expiry_date);
      await axiosAuth().post("/hr/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento subido");
      setShowUpload(false);
      setUploadFile(null);
      setUploadForm({ category: "otro", description: "", expiry_date: "" });
      loadDocs();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axiosAuth().get(`/hr/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };
  const formatSize = (bytes) => {
    if (!bytes) return "-";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const catMap = {};
  categories.forEach(c => { catMap[c.value] = c.label; });

  return (
    <div className="space-y-3" data-testid="employee-documents">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{docs.length} documento(s) asociados</p>
        <Button size="sm" variant="outline" className="text-xs rounded-sm h-7" onClick={() => setShowUpload(true)} data-testid="btn-emp-upload-doc">
          <Upload className="w-3 h-3 mr-1" /> Subir
        </Button>
      </div>

      {loading ? (
        <p className="text-center text-xs text-slate-400 py-4">Cargando...</p>
      ) : docs.length === 0 ? (
        <p className="text-center text-xs text-slate-400 py-4">Sin documentos asociados</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {docs.map(d => {
            const st = STATE_CONFIG[d.state] || STATE_CONFIG.pendiente;
            return (
              <div key={d.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm" data-testid={`emp-doc-${d.id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <File className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{d.filename}</p>
                    <p className="text-[10px] text-slate-400">{catMap[d.category] || d.category} &middot; {formatDate(d.created_at)} &middot; {formatSize(d.size_bytes)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Badge variant="outline" className={`text-[8px] ${st.bg}`}>{st.label}</Badge>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDownload(d)} title="Descargar" data-testid={`btn-emp-download-${d.id}`}>
                    <Download className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline upload form */}
      {showUpload && (
        <div className="border rounded-sm p-3 space-y-2 bg-white" data-testid="emp-upload-form">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-slate-500 font-semibold">Categoria</Label>
              <Select value={uploadForm.category} onValueChange={v => setUploadForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="h-7 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase text-slate-500 font-semibold">Caducidad</Label>
              <Input type="date" value={uploadForm.expiry_date} onChange={e => setUploadForm(p => ({ ...p, expiry_date: e.target.value }))} className="h-7 text-xs rounded-sm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-500 font-semibold">Descripcion</Label>
            <Input value={uploadForm.description} onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))} className="h-7 text-xs rounded-sm" placeholder="Descripcion breve..." />
          </div>
          <div
            className="border border-dashed border-slate-300 rounded-sm p-2 text-center cursor-pointer hover:border-slate-400"
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
            {uploadFile ? (
              <span className="text-xs">{uploadFile.name} ({formatSize(uploadFile.size)})</span>
            ) : (
              <span className="text-[10px] text-slate-400">Seleccionar archivo (PDF, JPG, PNG, DOCX, XLSX)</span>
            )}
          </div>
          <div className="flex justify-end gap-1">
            <Button variant="outline" size="sm" className="text-[10px] rounded-sm h-6 px-2" onClick={() => { setShowUpload(false); setUploadFile(null); }}>Cancelar</Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] rounded-sm h-6 px-2" onClick={handleUpload} disabled={uploading} data-testid="btn-emp-confirm-upload">
              {uploading ? "Subiendo..." : "Subir"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
